import { Suspense, Fragment } from "react";
import { Switch, Route, Router as WouterRouter, useLocation } from "wouter";
import type { QueryClient } from "@tanstack/react-query";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { CookieBanner } from "@/components/cookie-banner";
import { EarlyAccessModal } from "@/components/early-access-modal";
import { Navigation } from "@/components/navigation-single-page";
import { useAnalytics } from "./hooks/use-analytics";
import { I18nextProvider } from "react-i18next";
import type { i18n as I18nInstance } from "i18next";
import browserI18n from "./i18n";
import { LanguageProvider } from "./contexts/LanguageContext";
import * as ReactHelmetAsyncModule from "react-helmet-async";
import { useAnalyticsConsent } from "@/analytics/use-analytics";
import { getClientRouteComponent, getNotFoundComponent, type RouteComponentOverrides } from "./client-routes";
import {
  AVAILABLE_LANGUAGES,
  ROUTE_DEFINITIONS,
  getLanguageFromPath,
  getRouteDefinition,
  localizePath,
  type LanguageCode,
} from "./site-config";
import { SEOHead } from "@/components/seo-head";
import { queryClient as browserQueryClient } from "./lib/queryClient";

const helmetAsyncCompat = ReactHelmetAsyncModule as Record<string, any>;
const HelmetProvider =
  helmetAsyncCompat.HelmetProvider ||
  helmetAsyncCompat["default"]?.HelmetProvider;

function RouteLoading() {
  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-24 text-sm text-muted-foreground">
      Loading...
    </div>
  );
}

function AppRoutes({ routeComponentOverrides }: { routeComponentOverrides?: RouteComponentOverrides }) {
  useAnalytics();
  const NotFoundComponent = getNotFoundComponent(routeComponentOverrides);

  return (
    <Suspense fallback={<RouteLoading />}>
      <Switch>
        {ROUTE_DEFINITIONS.map((route) => {
          const Component = getClientRouteComponent(route.path, routeComponentOverrides);
          const localizedPaths = AVAILABLE_LANGUAGES.map((language) => localizePath(route.path, language));

          return (
            <Fragment key={route.path}>
              {localizedPaths.map((path) => (
                <Route key={path} path={path} component={Component} />
              ))}
            </Fragment>
          );
        })}
        <Route component={NotFoundComponent} />
      </Switch>
    </Suspense>
  );
}

function AppFrame({ routeComponentOverrides }: { routeComponentOverrides?: RouteComponentOverrides }) {
  useAnalyticsConsent();
  const [location] = useLocation();
  const currentRoute = getRouteDefinition(location);
  const shouldRenderGlobalNavigation = currentRoute?.renderGlobalNavigation ?? true;

  return (
    <>
      <SEOHead pathname={location} />
      {shouldRenderGlobalNavigation ? <Navigation /> : null}
      <AppRoutes routeComponentOverrides={routeComponentOverrides} />
      <EarlyAccessModal />
      <Toaster />
      <CookieBanner />
    </>
  );
}

interface AppProps {
  ssrPath?: string;
  initialLanguage?: LanguageCode;
  queryClient?: QueryClient;
  i18nInstance?: I18nInstance;
  helmetContext?: object;
  routeComponentOverrides?: RouteComponentOverrides;
}

function App({
  ssrPath,
  initialLanguage,
  queryClient,
  i18nInstance,
  helmetContext,
  routeComponentOverrides,
}: AppProps) {
  const resolvedPath =
    ssrPath || (typeof window !== "undefined" ? window.location.pathname : "/");
  const resolvedLanguage = initialLanguage || getLanguageFromPath(resolvedPath);

  return (
    <HelmetProvider context={helmetContext}>
      <I18nextProvider i18n={i18nInstance || browserI18n}>
        <QueryClientProvider client={queryClient || browserQueryClient}>
          <TooltipProvider>
            <WouterRouter ssrPath={ssrPath}>
              <LanguageProvider initialLanguage={resolvedLanguage}>
                <AppFrame routeComponentOverrides={routeComponentOverrides} />
              </LanguageProvider>
            </WouterRouter>
          </TooltipProvider>
        </QueryClientProvider>
      </I18nextProvider>
    </HelmetProvider>
  );
}

export default App;
